import { ValidationErrorsBIC, ValidationErrorsIBAN, validateBIC, validateIBAN } from 'ibantools';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { CountryCode } from '@Common/countries/iso_codes';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodBankAccountIban = z
  .string()
  .transform((val) => val.replaceAll(' ', '').trim())
  .superRefine((val, ctx) => {
    const validationResult = validateIBAN(val);

    if (!validationResult.valid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: validationResult.errorCodes
          .map((errorCode) => stringifyIbanValidationError(errorCode))
          .join('; '),
      });
    }
  })
  .meta({ override: { type: 'string' } });

export const ZodBankAccountBic = ZodTrimmedString.superRefine((val, ctx) => {
  const validationResult = validateBIC(val);

  if (!validationResult.valid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: validationResult.errorCodes
        .map((errorCode) => stringifyBicValidationError(errorCode))
        .join('; '),
    });
  }
});

export const ZodCustomerBankAccount = z.object({
  iban: ZodBankAccountIban,
  bic: ZodBankAccountBic,
});

function stringifyIbanValidationError(errorCode: ValidationErrorsIBAN) {
  return match(errorCode)
    .with(ValidationErrorsIBAN.NoIBANProvided, () => 'Not an IBAN')
    .with(ValidationErrorsIBAN.NoIBANCountry, () => 'Could not get country code from IBAN')
    .with(ValidationErrorsIBAN.WrongBBANLength, () => 'Wrong BBAN length')
    .with(ValidationErrorsIBAN.WrongBBANFormat, () => 'Wrong BBAN format')
    .with(ValidationErrorsIBAN.ChecksumNotNumber, () => 'Checksum is not a number')
    .with(ValidationErrorsIBAN.WrongIBANChecksum, () => 'Wrong IBAN checksum')
    .with(
      ValidationErrorsIBAN.WrongAccountBankBranchChecksum,
      () => 'Wrong account bank branch checksum',
    )
    .with(ValidationErrorsIBAN.QRIBANNotAllowed, () => 'QR-IBAN is not allowed')
    .otherwise((val) => `Unknown error - ${val}`);
}

function stringifyBicValidationError(errorCode: ValidationErrorsBIC) {
  return match(errorCode)
    .with(ValidationErrorsBIC.NoBICProvided, () => 'Not a BIC')
    .with(ValidationErrorsBIC.NoBICCountry, () => 'Could not get country code from BIC')
    .with(ValidationErrorsBIC.WrongBICFormat, () => 'Wrong BIC format')
    .otherwise((val) => `Unknown error - ${val}`);
}

const SEPAZoneCountries: CountryCode[] = [
  'AL',
  'AD',
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GI',
  'GR',
  'GG',
  'HU',
  'IS',
  'IE',
  'IM',
  'IT',
  'JE',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'MD',
  'MC',
  'ME',
  'NL',
  'MK',
  'NO',
  'PL',
  'PT',
  'RO',
  'SM',
  'RS',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH',
  'GB',
  'VA',
];

function isSEPAZoneCountry(countryCode: string): boolean {
  return SEPAZoneCountries.some((code) => code === countryCode);
}

// BIC country codes for SEPA territories whose IBANs use another country's prefix (DK/FR).
const SEPAZoneBicCountryCodes: CountryCode[] = ['FO', 'GL', 'GF', 'GP', 'MQ', 'RE', 'YT'];

function isSEPAZoneBicCountry(countryCode: string): boolean {
  return (
    isSEPAZoneCountry(countryCode) || SEPAZoneBicCountryCodes.some((code) => code === countryCode)
  );
}

export function isSEPAZoneIBAN(iban: string): boolean {
  return isSEPAZoneCountry(iban.slice(0, 2));
}

export function isSEPAZoneBic(bic: string): boolean {
  const normalized = bic.replaceAll(' ', '').trim();
  return normalized.length >= 6 && isSEPAZoneBicCountry(normalized.slice(4, 6).toUpperCase());
}

export const ZodIntlAccountNumber = ZodTrimmedString.refine(
  (accountNumber) => !ZodBankAccountIban.safeParse(accountNumber).success,
  { message: 'IBAN must be submitted with account_type iban' },
);

export const ZodNonSepaIntlBic = ZodBankAccountBic.refine((bic) => !isSEPAZoneBic(bic), {
  message: 'SEPA-zone bank accounts must use account_type iban',
});

export const ZodSepaBankAccountIban = ZodBankAccountIban.refine((iban) => isSEPAZoneIBAN(iban), {
  message: 'IBAN is not from a SEPA zone country',
}).meta({ override: { type: 'string' } });

export const ZodSEPABankAccount = z.object({
  iban: ZodSepaBankAccountIban,
  bic: ZodBankAccountBic,
});

export const ZodUkSortCode = ZodTrimmedString.transform((val) => val.replace(/[-\s]/g, ''))
  .refine((val) => /^\d{6}$/.test(val), {
    message: 'Sort code should contain exactly 6 digits',
  })
  .meta({ override: { type: 'string' } });

export const ZodUkAccountNumber = ZodTrimmedString.transform((val) => val.replace(/\s/g, ''))
  .refine((val) => /^\d{8}$/.test(val), {
    message: 'Account number should contain exactly 8 digits',
  })
  .meta({ override: { type: 'string' } });
